"use client";

import type { Facet } from "@apptus/esales-api";
import type { Market } from "@features/market/config/i18n-config";
import { Button } from "@ui/button";
import {
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@ui/dialog";
import { useSyncExternalStore } from "react";
import SearchFilterPanel from "./SearchFilterPanel";

const DESKTOP_QUERY = "(min-width: 768px)";

function useIsDesktop() {
  return useSyncExternalStore(
    (onChange) => {
      const media = window.matchMedia(DESKTOP_QUERY);
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    },
    () => window.matchMedia(DESKTOP_QUERY).matches,
    () => true,
  );
}

export default function SearchFilters({
  facets,
  market,
  title,
  closeLabel,
  clearLabel,
}: {
  facets: Facet[];
  market: Market;
  title: string;
  closeLabel: string;
  clearLabel: string;
}) {
  const desktop = useIsDesktop();
  if (facets.length === 0) return null;

  if (desktop) {
    return (
      <SearchFilterPanel
        facets={facets}
        market={market}
        clearLabel={clearLabel}
        className="flex min-w-72 flex-col gap-y-4 border-r max-md:hidden"
      />
    );
  }

  return (
    <DialogContent
      className="h-full max-w-4/5"
      position="top-left"
      animate="from-left"
    >
      <DialogHeader className="border-b">
        <DialogTitle>{title}</DialogTitle>
        <DialogClose asChild>
          <Button variant="ghost" size="auto">
            {closeLabel}
          </Button>
        </DialogClose>
      </DialogHeader>
      <SearchFilterPanel
        facets={facets}
        market={market}
        clearLabel={clearLabel}
        className="flex flex-col gap-y-4"
        clearClassName="mx-4 mt-4"
      />
    </DialogContent>
  );
}
